
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../base/Button';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [language, setLanguage] = useState('EN');
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // const navItems = [
  //   { name: 'Home', path: '/' },
  //   { name: 'About', path: '/about' },
  //   { name: 'Products', path: '/products' },
  //   { name: 'Blog', path: '/blog' },
  //   { name: 'Contact', path: '/contact' }
  // ];

  const menuItems = [
    {
      name: 'Home',
      path: '/',
      hasSubmenu: false
    },
    {
      name: 'Products',
      path: '/products',
      hasSubmenu: true,
      submenu: [
        { name: 'Frozen Fruits & Vegetables', path: '/products/frozen' },
        { name: 'Freeze-Dried Products', path: '/products/freeze-dried' },
        { name: 'Fruit Purees & Concentrates', path: '/products/purees' },
        { name: 'Juices', path: '/products/juices' },
        { name: 'Dried Herbs & Spices', path: '/products/herbs' }
      ]
    },
    {
      name: 'About Us',
      path: '/about',
      hasSubmenu: false
    },
    {
      name: 'Services',
      path: '/services',
      hasSubmenu: false
    },
    {
      name: 'Blog',
      path: '/blog',
      hasSubmenu: false
    },
    {
      name: 'Contact',
      path: '/contact',
      hasSubmenu: false
    }
  ];

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ 
        duration: 0.8, 
        ease: [0.25, 0.46, 0.45, 0.94],
        delay: 0.2 
      }}
      className={`fixed w-full top-0 z-50 transition-all duration-500 ease-out ${
        isScrolled 
          ? 'bg-white/80 backdrop-blur-md shadow-lg border-b border-gray-100/20' 
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.div 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center cursor-pointer"
            onClick={() => navigate('/')}
          >
            <motion.div 
              className="w-12 h-12 bg-gradient-to-r from-primary-600 to-green-600 rounded-lg flex items-center justify-center mr-3"
              whileHover={{ rotate: 5 }}
              transition={{ duration: 0.3 }}
            >
              <i className="ri-leaf-line text-white text-xl"></i>
            </motion.div>
            <span className={`text-2xl font-bold bg-gradient-to-r from-primary-600 to-green-600 bg-clip-text text-transparent transition-all duration-300 ${
              isScrolled ? 'opacity-100' : 'opacity-90'
            }`}>
              FrozenExport
            </span>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {menuItems.map((item, index) => (
              <div key={item.name} className="relative group">
                <motion.button
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index + 0.3 }}
                  onClick={() => navigate(item.path)}
                  className={`relative text-gray-700 hover:text-primary-600 font-medium transition-all duration-300 flex items-center gap-1 py-2 ${
                    location.pathname === item.path ? 'text-primary-600' : ''
                  } ${!isScrolled ? 'text-gray-800 hover:text-primary-500' : ''}`}
                >
                  {item.name}
                  {item.hasSubmenu && (
                    <motion.i 
                      className="ri-arrow-down-s-line text-sm"
                      animate={{ rotate: 0 }}
                      whileHover={{ rotate: 180 }}
                      transition={{ duration: 0.3 }}
                    />
                  )}
                  
                  {/* Animated underline */}
                  <motion.div
                    className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-primary-600 to-green-600 rounded-full"
                    initial={{ width: 0 }}
                    whileHover={{ width: '100%' }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  />
                </motion.button>
                
                {item.hasSubmenu && (
                  <motion.div 
                    className="absolute top-full left-0 mt-2 w-64 bg-white/95 backdrop-blur-md rounded-xl shadow-xl border border-gray-100/50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0"
                    initial={{ opacity: 0, y: 10 }}
                    whileHover={{ opacity: 1, y: 0 }}
                  >
                    <div className="p-2">
                      {item.submenu?.map((subItem, subIndex) => (
                        <motion.button
                          key={subItem.name}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: subIndex * 0.05 }}
                          onClick={() => navigate(subItem.path)}
                          className="w-full text-left px-4 py-3 text-gray-600 hover:text-primary-600 hover:bg-primary-50/80 rounded-lg transition-all duration-200 group/item"
                        >
                          <span className="group-hover/item:translate-x-1 transition-transform duration-200 inline-block">
                            {subItem.name}
                          </span>
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>
            ))}
          </nav>

          {/* Language Switcher & CTA */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="hidden lg:flex items-center space-x-4"
          >
            <div className={`flex items-center rounded-full p-1 transition-all duration-300 ${
              isScrolled ? 'bg-gray-100/80' : 'bg-white/20 backdrop-blur-sm'
            }`}>
              <button
                onClick={() => setLanguage('EN')}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-300 ${
                  language === 'EN' 
                    ? 'bg-white text-primary-600 shadow-sm' 
                    : 'text-gray-600 hover:text-primary-600'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLanguage('TR')}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-300 ${
                  language === 'TR' 
                    ? 'bg-white text-primary-600 shadow-sm' 
                    : 'text-gray-600 hover:text-primary-600'
                }`}
              >
                TR
              </button>
            </div>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button 
                onClick={() => navigate('/contact')}
                className="bg-gradient-to-r from-primary-600 to-green-600 hover:from-primary-700 hover:to-green-700 text-white shadow-lg hover:shadow-xl"
              >
                Get Quote
                <motion.i 
                  className="ri-arrow-right-line ml-2"
                  whileHover={{ x: 3 }}
                  transition={{ duration: 0.2 }}
                />
              </Button>
            </motion.div>
          </motion.div>

          {/* Mobile Menu Button */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-all duration-300 ${
              isScrolled 
                ? 'text-gray-700 hover:text-primary-600 hover:bg-gray-100/50' 
                : 'text-gray-800 hover:text-primary-600 hover:bg-white/20'
            }`}
          >
            <motion.i 
              className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl`}
              animate={{ rotate: isMenuOpen ? 90 : 0 }}
              transition={{ duration: 0.3 }}
            />
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg-hidden bg-white/95 backdrop-blur-md border-t border-gray-200/50 shadow-lg"
          >
            <div className="container mx-auto px-4 py-4">
              <nav className="space-y-2">
                {menuItems.map((item, index) => (
                  <motion.button
                    key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => {
                      navigate(item.path);
                      setIsMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:text-primary-600 hover:bg-primary-50/80 rounded-lg transition-all duration-200 group"
                  >
                    <span className="group-hover:translate-x-2 transition-transform duration-200 inline-block">
                      {item.name}
                    </span>
                  </motion.button>
                ))}
              </nav>
              
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200/50"
              >
                <div className="flex items-center bg-gray-100/80 rounded-full p-1">
                  <button
                    onClick={() => setLanguage('EN')}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-300 ${
                      language === 'EN' ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600'
                    }`}
                  >
                    EN
                  </button>
                  <button
                    onClick={() => setLanguage('TR')}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-300 ${
                      language === 'TR' ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600'
                    }`}
                  >
                    TR
                  </button>
                </div>
                <Button 
                  size="sm" 
                  onClick={() => {
                    navigate('/contact');
                    setIsMenuOpen(false);
                  }}
                  className="bg-gradient-to-r from-primary-600 to-green-600 hover:from-primary-700 hover:to-green-700"
                >
                  Get Quote
                </Button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
